/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Component/TestPlansHubRefreshPortalComponent" />
import * as React from "react";
import { TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { getSuitesModule } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesModule";
import { TestHubRefreshModule } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshModule";
import * as Resources from "Test/TestPlansHubRefresh/TestHub/Resources";
import { getDataProviderContent } from "Test/TestPlansHubRefresh/TestHub/Util";
import { TestPlansHubRefreshData } from "TestPlanCommon/Contracts/TestPlan";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation, IVssLocationService } from "VSS/Platform/Location";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Uri } from "VSS/Platform/Util/Url";
import { IContentViewObserverModuleArgs, IFilterArgs } from "VSS/Redux/Modules/ContentViewObserver/ContentViewObserverContracts";
import { IRootProviderProps, RootProvider } from "VSS/Redux/RootProvider";
import { Filter, FilterContext } from "VSSUI/Utilities/Filter";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { TestPlansHubRefreshConnectedView } from "./TestPlansHubRefreshConnectedView";

export interface ITestHubSagaContext {
    pageContext: IVssPageContext;
}

/**
 * TestPlansHubRefreshPortalComponent: The root component which renders the entire test plans hub view.
 */
export class TestPlansHubRefreshPortalComponent extends VssComponent {
    private _rootProviderProps: IRootProviderProps<ITestHubSagaContext, any>;
    private _selectedTab: ObservableValue<string>;
    private _opId: number;
    private _filterArgs: IFilterArgs | undefined;
    private _data: TestPlansHubRefreshData;
    private _mineAllPageUrl: string = "";

    constructor(props: never, context: IVssComponentContext) {
        super(props, context);

        this._data = getDataProviderContent(context.pageContext);
        let defaultPivot = "";
        if (this._data && !this._data.errorMessage) {
            defaultPivot = this._data.selectedPivotId;

            let registryEntries = {
                SelectedPivotId: defaultPivot
            };
            const settingsService: ISettingsService = context.pageContext.getService<ISettingsService>("ISettingsService");
            const tfsPageService: ITfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();

            if (tfsPageData && tfsPageData.project) {
                settingsService.setEntries(registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
            }
        } else {
            const pageContext: IVssPageContext = context.pageContext;
            const tfsPageService: ITfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            let project = "";
            if (tfsPageData && tfsPageData.project) {
                project = tfsPageData.project.name;
            }
            const routeValues: { [key: string]: string } = {
                pivot: TestHubRefreshConstants.testPlanAll,
                project: project
            };
            const locationService: IVssLocationService = pageContext.getService<IVssLocationService>("IVssLocationService");
            this._mineAllPageUrl = locationService.routeUrl(TestHubRefreshConstants.allMineRouteContributionId, routeValues);
        }

        this._selectedTab = new ObservableValue(defaultPivot);
        this._rootProviderProps = this._getRootProviderProps();
    }

    public render(): JSX.Element {
        if (!this._filterArgs) {
            this._filterArgs = {
                filter: new Filter(),
                filterToggled: new ObservableValue<boolean>(false)
            };
        }

        const contentViewObserverModuleArgs: IContentViewObserverModuleArgs = {
            filterArgs: this._filterArgs,
            selectedTab: this._selectedTab
        };
        let refreshView: JSX.Element = (<></>) as JSX.Element;
        if (this._data) {
            if (!this._data.errorMessage) {
                refreshView = <TestPlansHubRefreshConnectedView selectedTab={this._selectedTab} opId={this._opId}/>;
            } else {
                const image = getAssetLocation(this.context.pageContext, TestHubRefreshConstants.NoPlanImagePath);
                refreshView = (
                    <ZeroData
                        imagePath={image}
                        className="testplans-hub-no-plans"
                        imageAltText={this._data.errorMessage}
                        primaryText={this._data.errorMessage}
                        secondaryText={Resources.PlanDoesNotExistSecondaryText}
                        actionType={ZeroDataActionType.ctaButton}
                        actionText={Resources.TestPlanPage}
                        onActionClick={e => {
                            const openedWindow = window.open(this._mineAllPageUrl, "_self");
                            if (openedWindow) {
                                openedWindow.opener = null;
                            }
                        }}
                    />
                );
            }
        } else {
            const image = getAssetLocation(this.context.pageContext, TestHubRefreshConstants.NoPlanImagePath);
            refreshView = (
                <ZeroData
                    imagePath={image}
                    className="testplans-hub-no-plans"
                    imageAltText={Resources.PlanDoesNotExist}
                    primaryText={Resources.PlanDoesNotExist}
                    secondaryText={Resources.PlanDoesNotExistSecondaryText}
                    actionType={ZeroDataActionType.ctaButton}
                    actionText={Resources.TestPlanPage}
                    onActionClick={e => {
                        const openedWindow = window.open(this._mineAllPageUrl, "_self");
                        if (openedWindow) {
                            openedWindow.opener = null;
                        }
                    }}
                />
            );
        }
        return (
            <RootProvider {...this._rootProviderProps} contentViewObserverModuleArgs={contentViewObserverModuleArgs}>
                <FilterContext.Provider value={this._filterArgs}>
                    <div className="testplans-hub-refresh-portal-component flex justify-center flex-grow flex-column">{refreshView}</div>
                </FilterContext.Provider>
            </RootProvider>
        );
    }

    private _getRootProviderProps(): IRootProviderProps<ITestHubSagaContext, any> {
        const uri = new Uri(window.location.href);

        const modules = [getSuitesModule(this._data), TestHubRefreshModule];
        const opId = uri.getQueryParam(TestHubRefreshConstants.QUERY_PARAM_OP_ID);
        if (opId) {
            this._opId = parseInt(opId);
        }
        
        return {
            initialState: {},
            sagaContext: { pageContext: this.context.pageContext },
            scenarioName: "TestManagement",
            modules: modules,
            postTTIModules: [
                {
                    contributionId: "ms.vss-test-web.testhub-refresh-post-tti",
                    reduxModuleId: "test-hub-refresh-post-tti-module"
                }
            ]
        };
    }
}

VssComponent.register("testPlansHubRefreshPortalComponent", TestPlansHubRefreshPortalComponent);
